import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Button } from '../ui/BaseComponents';
import { notifySuccess, notifyWarning } from '../../services/notificationService';
import DataCard from '../ui/DataCard';
import { SmartTextRenderer, EnhancedCodeBlock } from '../ui/MathRenderer';
import { QuestionMedia } from '../ui/QuestionMedia';

/**
 * LLMPreviewTab - Tab for previewing and editing generated questions
 * Clean design without emoticons, following the V2 design system
 */
function LLMPreviewTab({
  generatedQuestions = [],  // Default to empty array for null safety
  setGeneratedQuestions,
  validationErrors = {},
  editingIndex,
  editedQuestion,
  setEditedQuestion,
  
  // Actions
  findDuplicates,
  validateAllQuestions,
  onRerollQuestion,
  onRephraseQuestion,
  onGenerateVariation,
  onToggleMultipleChoice,
  onShuffleAnswers,
  onStartEditing,
  onSaveEdit,
  onCancelEdit,
  onSaveToSession,
  onDownloadQuestions,
  onRefineQuestion,
  isGenerating,
  
  // Sessions
  existingSessions = [],  // Default to empty array for null safety
  selectedSession,
  setSelectedSession,
  
  // Source template context (from TemplateEditor navigation)
  sourceTemplateId,
  sourceTemplateName
}) {
  // Extra null safety for arrays
  const questions = generatedQuestions || [];
  const errors = validationErrors || {};
  const sessions = existingSessions || [];

  const handleDelete = (index) => {
    const newQuestions = questions.filter((_, i) => i !== index);
    setGeneratedQuestions(newQuestions);
  };

  const handleCheckDuplicates = () => {
    const duplicates = findDuplicates();
    if (duplicates.length === 0) {
      notifySuccess('Keine Duplikate gefunden!');
    } else {
      const message = duplicates.map(d => 
        `Frage ${d.index1 + 1} und ${d.index2 + 1}: ${Math.round(d.similarity * 100)}% ähnlich`
      ).join(', ');
      notifyWarning(`${duplicates.length} mögliche Duplikate: ${message}`);
    }
  };

  const handleValidate = () => {
    const validationErrors = validateAllQuestions();
    const errorCount = Object.keys(validationErrors).length;
    if (errorCount === 0) {
      notifySuccess('Alle Fragen sind gültig!');
    } else {
      notifyWarning(`${errorCount} Fragen haben Fehler`);
    }
  };

  // Empty state
  if (questions.length === 0) {
    return (
      <Card className="text-center py-12">
        <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Keine Fragen vorhanden
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          Generiere zuerst Fragen im Tab "Generieren"
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action Bar */}
      <Card padding="sm">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {questions.length} Fragen generiert
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCheckDuplicates}
              className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              Duplikate prüfen
            </button>
            <button
              onClick={handleValidate}
              className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              Validieren
            </button>
            <button
              onClick={() => {
                // Shuffle answers for ALL questions
                questions.forEach((_, idx) => onShuffleAnswers(idx));
              }}
              className="text-sm px-3 py-1.5 border border-primary-300 dark:border-primary-600 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
              Alle mischen
            </button>
            <button
              onClick={() => setGeneratedQuestions([])}
              className="text-sm px-3 py-1.5 border border-danger-300 dark:border-danger-600 rounded-lg hover:bg-danger-50 dark:hover:bg-danger-900/30 text-danger-600 dark:text-danger-400"
            >
              Alle löschen
            </button>
          </div>
        </div>
      </Card>

      {/* Questions Grid */}
      <div className="grid grid-cols-1 gap-4">
        {questions.map((question, index) => (
          <QuestionPreviewCard
            key={index}
            question={question}
            index={index}
            isEditing={editingIndex === index}
            editedQuestion={editedQuestion}
            setEditedQuestion={setEditedQuestion}
            errors={errors[index]}
            isGenerating={isGenerating}
            onReroll={() => onRerollQuestion(index)}
            onRephrase={() => onRephraseQuestion?.(index)}
            onVariation={() => onGenerateVariation(index)}
            onToggleMultipleChoice={() => onToggleMultipleChoice(index)}
            onShuffleAnswers={() => onShuffleAnswers(index)}
            onStartEditing={() => onStartEditing(index)}
            onSaveEdit={onSaveEdit}
            onCancelEdit={onCancelEdit}
            onDelete={() => handleDelete(index)}
            onRefine={() => onRefineQuestion?.(index)}
          />
        ))}
      </div>

      {/* Save Section */}
      <SaveSection
        existingSessions={sessions}
        selectedSession={selectedSession}
        setSelectedSession={setSelectedSession}
        onSaveToSession={onSaveToSession}
        onDownloadQuestions={onDownloadQuestions}
        sourceTemplateId={sourceTemplateId}
        sourceTemplateName={sourceTemplateName}
      />
    </div>
  );
}

/**
 * SaveSection - Section for saving questions with new template creation
 */
function SaveSection({
  existingSessions = [],  // Default to empty array
  selectedSession,
  setSelectedSession,
  onSaveToSession,
  onDownloadQuestions,
  sourceTemplateId,
  sourceTemplateName
}) {
  // Ensure existingSessions is always an array
  const sessions = existingSessions || [];
  
  // If we have a source template, default to adding to existing
  const [isCreatingNew, setIsCreatingNew] = React.useState(
    sessions.length === 0 && !sourceTemplateId
  );
  const [newTemplateName, setNewTemplateName] = React.useState('');
  const [notification, setNotification] = React.useState(null);
  
  // Auto-select source template when coming from TemplateEditor
  React.useEffect(() => {
    if (sourceTemplateId && !selectedSession) {
      const templateExists = sessions.find(s => s.id === sourceTemplateId);
      if (templateExists) {
        setSelectedSession(sourceTemplateId);
        setIsCreatingNew(false);
      }
    }
  }, [sourceTemplateId, sessions, selectedSession, setSelectedSession]);
  
  // If we came from a template that's selected, show a hint
  const selectedTemplateName = React.useMemo(() => {
    if (selectedSession) {
      const found = sessions.find(s => s.id === selectedSession);
      return found?.name || sourceTemplateName || null;
    }
    return null;
  }, [selectedSession, sessions, sourceTemplateName]);

  const handleSave = async () => {
    if ((isCreatingNew || sessions.length === 0) && newTemplateName.trim()) {
      await onSaveToSession(null, newTemplateName.trim());
      setNewTemplateName('');
      setIsCreatingNew(false);
      setNotification({ type: 'success', message: `Vorlage "${newTemplateName}" erstellt!` });
    } else if (selectedSession) {
      await onSaveToSession(selectedSession);
      const sessionName = sessions.find(s => s.id === selectedSession)?.name || selectedSession;
      setNotification({ type: 'success', message: `Fragen zu "${sessionName}" hinzugefügt!` });
    }
    
    // Auto-hide notification after 3 seconds
    setTimeout(() => setNotification(null), 3000);
  };

  // Can save if: creating new with name OR selecting existing session
  const canSave = (isCreatingNew || sessions.length === 0) 
    ? newTemplateName.trim().length > 0 
    : !!selectedSession;

  return (
    <Card>
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
        Speichern
      </h3>
      
      {/* Source template hint - show if came from a template */}
      {sourceTemplateId && selectedTemplateName && !isCreatingNew && (
        <div className="mb-4 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-primary-700 dark:text-primary-300">
              Fragen werden zu <span className="font-semibold">"{selectedTemplateName}"</span> hinzugefügt
            </p>
          </div>
        </div>
      )}
      
      {/* Notification */}
      {notification && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
          notification.type === 'success' 
            ? 'bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300 border border-success-200 dark:border-success-700'
            : 'bg-danger-50 dark:bg-danger-900/20 text-danger-700 dark:text-danger-300 border border-danger-200 dark:border-danger-700'
        }`}>
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {notification.type === 'success' ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            )}
          </svg>
          <span className="font-medium">{notification.message}</span>
        </div>
      )}

      {/* Toggle: Existing or New - always show both options */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setIsCreatingNew(false)}
          disabled={sessions.length === 0}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium theme-transition ${
            !isCreatingNew && sessions.length > 0
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-2 border-primary-500'
              : sessions.length === 0
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-2 border-transparent cursor-not-allowed'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-2 border-transparent'
          }`}
        >
          Vorhandene Vorlage {sessions.length === 0 && '(keine)'}
        </button>
        <button
          onClick={() => setIsCreatingNew(true)}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium theme-transition ${
            isCreatingNew || sessions.length === 0
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-2 border-primary-500'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-2 border-transparent'
          }`}
        >
          + Neue Vorlage
        </button>
      </div>

      {/* Existing Template Select or New Template Input */}
      {isCreatingNew || sessions.length === 0 ? (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Name der neuen Vorlage:
          </label>
          <input
            type="text"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            placeholder="z.B. 'Python Grundlagen' oder 'SQL Prüfung 2026'"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSave) {
                handleSave();
              }
            }}
          />
        </div>
      ) : (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            In Vorlage speichern:
          </label>
          <select
            value={selectedSession || ''}
            onChange={(e) => setSelectedSession(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Vorlage auswählen...</option>
            {sessions.map(session => (
              <option key={session.id} value={session.id}>
                {session.name} ({session.questionCount} Fragen)
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={!canSave}
          className={`flex-1 py-2.5 px-4 rounded-lg font-medium theme-transition ${
            !canSave
              ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              : 'bg-success-600 text-white hover:bg-success-700'
          }`}
        >
          {isCreatingNew || sessions.length === 0 ? 'Neue Vorlage erstellen' : 'Zu Vorlage hinzufügen'}
        </button>
        
        <button
          onClick={onDownloadQuestions}
          className="flex-1 py-2.5 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 theme-transition"
        >
          Als JSON herunterladen
        </button>
      </div>
    </Card>
  );
}

/**
 * QuestionPreviewCard - Individual question card without emoticons
 */
function QuestionPreviewCard({
  question,
  index,
  isEditing,
  editedQuestion,
  setEditedQuestion,
  errors,
  isGenerating,
  onReroll,
  onRephrase,
  onVariation,
  onToggleMultipleChoice,
  onShuffleAnswers,
  onStartEditing,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onRefine
}) {
  const q = question;
  const [showCodeBlockEditor, setShowCodeBlockEditor] = useState(!!q.codeBlock?.code);
  
  if (isEditing) {
    return (
      <Card className={errors ? 'border-danger-300 dark:border-danger-700' : ''}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Frage:
            </label>
            <input
              type="text"
              value={editedQuestion.question}
              onChange={(e) => setEditedQuestion({...editedQuestion, question: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Antwortoptionen:
            </label>
            {editedQuestion.options.map((opt, optIdx) => (
              <div key={optIdx} className="flex gap-2 mb-2">
                <span className="flex items-center justify-center w-8 h-10 text-sm font-medium text-gray-500 dark:text-gray-400">
                  {String.fromCharCode(65 + optIdx)}.
                </span>
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => {
                    const newOptions = [...editedQuestion.options];
                    newOptions[optIdx] = e.target.value;
                    setEditedQuestion({...editedQuestion, options: newOptions});
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <button
                  onClick={() => {
                    const newOptions = editedQuestion.options.filter((_, i) => i !== optIdx);
                    setEditedQuestion({...editedQuestion, options: newOptions});
                  }}
                  className="px-3 py-2 text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/30 rounded-lg"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
            <button
              onClick={() => {
                setEditedQuestion({
                  ...editedQuestion, 
                  options: [...editedQuestion.options, '']
                });
              }}
              className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              + Option hinzufügen
            </button>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Korrekte Antwort{editedQuestion.allowMultiple ? 'en' : ''}:
            </label>
            {editedQuestion.allowMultiple ? (
              /* Multiple Choice: Checkboxes */
              <div className="space-y-2">
                {editedQuestion.options.map((opt, idx) => (
                  <label key={idx} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editedQuestion.correctAnswerIndices?.includes(idx) ?? false}
                      onChange={(e) => {
                        const currentIndices = editedQuestion.correctAnswerIndices || [];
                        const newIndices = e.target.checked
                          ? [...currentIndices, idx].sort((a, b) => a - b)
                          : currentIndices.filter(i => i !== idx);
                        setEditedQuestion({...editedQuestion, correctAnswerIndices: newIndices});
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {String.fromCharCode(65 + idx)}. {opt.substring(0, 40)}{opt.length > 40 ? '...' : ''}
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              /* Single Choice: Radio buttons / Select */
              <select
                value={editedQuestion.correctAnswerIndices?.[0] ?? 0}
                onChange={(e) => setEditedQuestion({...editedQuestion, correctAnswerIndices: [parseInt(e.target.value)]})}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {editedQuestion.options.map((opt, idx) => (
                  <option key={idx} value={idx}>
                    {String.fromCharCode(65 + idx)}. {opt.substring(0, 40)}{opt.length > 40 ? '...' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          
          {/* Code Block Editor */}
          <div>
            <div className="flex items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Code-Block</label>
              <button
                type="button"
                onClick={() => setShowCodeBlockEditor(!showCodeBlockEditor)}
                className="ml-2 text-xs bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                {showCodeBlockEditor ? "Ausblenden" : "Einblenden"}
              </button>
            </div>
            
            {showCodeBlockEditor && (
              <div className="space-y-2">
                <select
                  value={editedQuestion.codeBlock?.language || 'javascript'}
                  onChange={(e) => setEditedQuestion({
                    ...editedQuestion,
                    codeBlock: {
                      ...(editedQuestion.codeBlock || {}),
                      language: e.target.value
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="javascript">JavaScript</option>
                  <option value="python">Python</option>
                  <option value="java">Java</option>
                  <option value="csharp">C#</option>
                  <option value="cpp">C++</option>
                  <option value="bash">Bash / Terminal</option>
                  <option value="sql">SQL</option>
                  <option value="html">HTML</option>
                  <option value="css">CSS</option>
                  <option value="json">JSON</option>
                  <option value="xml">XML</option>
                  <option value="plaintext">Plain Text</option>
                </select>
                
                <textarea
                  value={editedQuestion.codeBlock?.code || ''}
                  onChange={(e) => setEditedQuestion({
                    ...editedQuestion,
                    codeBlock: {
                      ...(editedQuestion.codeBlock || {}),
                      code: e.target.value
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={6}
                  placeholder="Code hier eingeben..."
                />
                
                {editedQuestion.codeBlock?.code && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Vorschau:</p>
                    <EnhancedCodeBlock
                      code={editedQuestion.codeBlock.code}
                      language={editedQuestion.codeBlock.language || 'javascript'}
                    />
                  </div>
                )}
                
                {editedQuestion.codeBlock?.code && (
                  <button
                    type="button"
                    onClick={() => setEditedQuestion({
                      ...editedQuestion,
                      codeBlock: { language: 'javascript', code: '' }
                    })}
                    className="text-xs text-danger-600 dark:text-danger-400 hover:text-danger-700 dark:hover:text-danger-300"
                  >
                    Code-Block entfernen
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Explanation Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Erklärung <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Diese Erklärung wird nach Beantwortung der Frage angezeigt
            </p>
            <textarea
              value={editedQuestion.explanation || ''}
              onChange={(e) => setEditedQuestion({
                ...editedQuestion,
                explanation: e.target.value
              })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              rows={3}
              placeholder="Erkläre hier, warum diese Antwort richtig ist..."
            />
          </div>
          
          <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onSaveEdit}
              className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700"
            >
              Speichern
            </button>
            <button
              onClick={onCancelEdit}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className={errors ? 'border-danger-300 dark:border-danger-700' : ''}>
      <div className="flex justify-between items-start gap-4">
        {/* Question Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3 mb-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300">
              {index + 1}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 dark:text-white">
                <SmartTextRenderer text={q.question} />
              </p>
              
              {/* Multiple Choice Badge - only show if question allows multiple answers */}
              {q.allowMultiple && (
                <div className="mt-2">
                  <span className="text-xs px-2 py-0.5 rounded bg-secondary-100 dark:bg-secondary-900/30 text-secondary-700 dark:text-secondary-300">
                    Multiple Choice
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Code Block */}
          {q.codeBlock && q.codeBlock.code && (
            <div className="mb-3">
              <EnhancedCodeBlock
                code={q.codeBlock.code}
                language={q.codeBlock.language}
              />
            </div>
          )}

          {/* Media/Image */}
          {q.media && (
            <div className="mb-3">
              <QuestionMedia media={q.media} />
            </div>
          )}

          {/* Errors */}
          {errors && (
            <div className="mb-3 text-xs text-danger-700 dark:text-danger-300 bg-danger-50 dark:bg-danger-900/30 p-2 rounded-lg">
              {errors.join(', ')}
            </div>
          )}

          {/* Options */}
          <div className="space-y-1.5">
            {q.options.map((opt, optIndex) => {
              // Always use correctAnswerIndices (the standard format)
              const isCorrect = q.correctAnswerIndices?.includes(optIndex) ?? optIndex === 0;
              
              return (
                <div
                  key={optIndex}
                  className={`flex items-center gap-2 text-sm ${
                    isCorrect 
                      ? 'text-success-700 dark:text-success-400 font-medium' 
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  <span className="w-5 text-center">{String.fromCharCode(65 + optIndex)}.</span>
                  <span className="flex-1"><SmartTextRenderer text={opt} /></span>
                  {isCorrect && (
                    <svg className="w-4 h-4 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button
            onClick={onStartEditing}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg theme-transition"
            title="Bearbeiten"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={onReroll}
            disabled={isGenerating}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-warning-600 dark:hover:text-warning-400 hover:bg-warning-50 dark:hover:bg-warning-900/30 rounded-lg theme-transition disabled:opacity-50"
            title="Komplett neu generieren"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button
            onClick={onRephrase}
            disabled={isGenerating}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg theme-transition disabled:opacity-50"
            title="Umformulieren (gleicher Inhalt)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
          </button>
          <button
            onClick={onVariation}
            disabled={isGenerating}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-secondary-600 dark:hover:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-900/30 rounded-lg theme-transition disabled:opacity-50"
            title="Variation erstellen (neue Frage hinzufügen)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          <button
            onClick={onToggleMultipleChoice}
            className={`p-2 rounded-lg theme-transition ${
              q.allowMultiple 
                ? 'text-secondary-600 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900/30' 
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
            title={q.allowMultiple ? 'Zu Single Choice' : 'Zu Multiple Choice'}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          <button
            onClick={onShuffleAnswers}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg theme-transition"
            title="Antworten mischen"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-danger-600 dark:hover:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/30 rounded-lg theme-transition"
            title="Löschen"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </Card>
  );
}

LLMPreviewTab.propTypes = {
  generatedQuestions: PropTypes.array.isRequired,
  setGeneratedQuestions: PropTypes.func.isRequired,
  validationErrors: PropTypes.object,
  editingIndex: PropTypes.number,
  editedQuestion: PropTypes.object,
  setEditedQuestion: PropTypes.func.isRequired,
  
  findDuplicates: PropTypes.func.isRequired,
  validateAllQuestions: PropTypes.func.isRequired,
  onRerollQuestion: PropTypes.func.isRequired,
  onRephraseQuestion: PropTypes.func,
  onGenerateVariation: PropTypes.func.isRequired,
  onToggleMultipleChoice: PropTypes.func.isRequired,
  onStartEditing: PropTypes.func.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onSaveToSession: PropTypes.func.isRequired,
  onDownloadQuestions: PropTypes.func.isRequired,
  onRefineQuestion: PropTypes.func,
  isGenerating: PropTypes.bool,
  
  existingSessions: PropTypes.array,
  selectedSession: PropTypes.string,
  setSelectedSession: PropTypes.func
};

QuestionPreviewCard.propTypes = {
  question: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isEditing: PropTypes.bool,
  editedQuestion: PropTypes.object,
  setEditedQuestion: PropTypes.func,
  errors: PropTypes.array,
  isGenerating: PropTypes.bool,
  onReroll: PropTypes.func.isRequired,
  onRephrase: PropTypes.func,
  onVariation: PropTypes.func.isRequired,
  onToggleMultipleChoice: PropTypes.func.isRequired,
  onStartEditing: PropTypes.func.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRefine: PropTypes.func
};

export default LLMPreviewTab;
